import React from 'react';

import { find } from 'lodash';
import { Controller, useForm } from 'react-hook-form';

import { ReportListApiResponse } from 'components/Dashboards/Reporting/reporting.types';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

import classes from './NewReportModal.module.scss';

interface NewReport {
    reportName: string;
}

interface Props {
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    reportsList: ReportListApiResponse[];
    onSubmit: (reportName: string) => Promise<void>;
    isLoading: boolean;
}

export const NewReportModal = ({ isModalOpen, setIsModalOpen, reportsList, onSubmit, isLoading }: Props) => {
    const hideModal = () => {
        setIsModalOpen(false);
    };

    const {
        handleSubmit,
        setValue,
        control,
        formState: { errors },
    } = useForm<NewReport>();

    const onSubmitForm = async (data: NewReport) => {
        await onSubmit(data.reportName);
        setIsModalOpen(false);
    };

    return (
        <Modal
            open={isModalOpen}
            onClose={hideModal}
            title={'Create New Report'}
            subtitle={'Give your report a name to get started'}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={hideModal}>
                        Cancel
                    </Button>
                    <Button
                        size={'large'}
                        variant={'primary'}
                        type={'submit'}
                        isLoading={isLoading}
                        disabled={isLoading}
                        onClick={handleSubmit(onSubmitForm)}
                    >
                        Create report
                    </Button>
                </>
            }
        >
            <div className={classes.newReportModal}>
                <form onSubmit={handleSubmit(onSubmitForm)}>
                    <div className={classes.inputWrapper}>
                        <Controller
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    placeholder={'Report Name'}
                                    maxLength={100}
                                    onClear={() => {
                                        setValue('reportName', '');
                                    }}
                                    id={'reportName'}
                                />
                            )}
                            name={`reportName`}
                            control={control}
                            rules={{
                                required: 'Name is Required',
                                validate: (value) =>
                                    find(reportsList, {
                                        name: value,
                                    }) === undefined || 'Report already exists',
                            }}
                            defaultValue={'Custom Report'}
                        />
                    </div>
                    {errors.reportName && <CustomizedFormErrorMessage text={errors.reportName.message} />}
                </form>
            </div>
        </Modal>
    );
};
